import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { ChatPage } from '../iframe/chat-page';
import type { ChatTheme } from '../iframe/types';
import { getOrCreateAnonymousId } from '@chanl/widget-sdk';
import type { ChanlUser, ClientContext } from '@chanl/widget-sdk';

/**
 * `/chat/:agentId` — the iframe chat UI route.
 *
 * The embed loader (`/widget/v1.js`) creates an iframe pointing here with the
 * agent ID as a path segment and chat config as query params. We map those
 * into the `<ChatPage>` component (see `../iframe/`).
 *
 * URL contract (must stay in sync with the loader's `mkChat()`):
 *   /chat/:agentId?theme=…&apiKey=…&baseUrl=…&branding=…&locale=…&name=…
 *   &externalId=…&userEmail=…&userName=…&userAvatarUrl=…&userAttributes=<base64>&userHash=…
 */
export function ChatRoute() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();

  const theme: ChatTheme = searchParams.get('theme') === 'light' ? 'light' : 'dark';

  // Parse identity from URL params. When `externalId` is present, activate the
  // widget identity path — the iframe's chat client forwards these to
  // POST /convo/boot where the backend verifies `userHash` + upserts
  // the Customer by externalId + exposes attributes as `customer:<key>`
  // prompt variables. Parse once via useMemo — the query params don't change
  // during the iframe's lifetime.
  const { user, userHash } = useMemo(() => {
    const externalId = searchParams.get('externalId');
    if (!externalId) return { user: undefined as ChanlUser | undefined, userHash: undefined as string | undefined };

    let attributes: Record<string, unknown> | undefined;
    const attrB64 = searchParams.get('userAttributes');
    if (attrB64) {
      try {
        // Browser base64 → JSON. Matches the loader's `btoa(JSON.stringify(...))`
        // encoding in `mkChat()`.
        const json = typeof atob === 'function' ? atob(attrB64) : '';
        if (json) attributes = JSON.parse(json) as Record<string, unknown>;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ChanlChat iframe] Failed to decode userAttributes:', err);
      }
    }

    return {
      user: {
        externalId,
        email: searchParams.get('userEmail') ?? undefined,
        name: searchParams.get('userName') ?? undefined,
        avatarUrl: searchParams.get('userAvatarUrl') ?? undefined,
        attributes,
      } satisfies ChanlUser,
      userHash: searchParams.get('userHash') ?? undefined,
    };
  }, [searchParams]);

  // Page-level client context forwarded by the embed loader. The loader runs
  // on the HOST page, so `ctxPageUrl`/`ctxReferrer`/`ctxUtm*` carry the
  // customer's page values — not the iframe's own URL. We rebuild the
  // `ClientContext` shape here and hand it to createSession as
  // `input.clientContext`, which the chat-client merges over the (wrong)
  // iframe-local page-level fields it collects itself (caller wins per-field).
  // Browser-level fields (userAgent/language/timezone/screen) are NOT
  // forwarded — they're correct from inside the iframe. See the loader's
  // `appendHostContextParams()` for the param → field mapping.
  const clientContext = useMemo<ClientContext | undefined>(() => {
    const pageUrl = searchParams.get('ctxPageUrl') ?? undefined;
    const referrer = searchParams.get('ctxReferrer') ?? undefined;

    const utmEntries: Array<[keyof NonNullable<ClientContext['utm']>, string | null]> = [
      ['source', searchParams.get('ctxUtmSource')],
      ['medium', searchParams.get('ctxUtmMedium')],
      ['campaign', searchParams.get('ctxUtmCampaign')],
      ['term', searchParams.get('ctxUtmTerm')],
      ['content', searchParams.get('ctxUtmContent')],
    ];
    const utm: NonNullable<ClientContext['utm']> = {};
    for (const [field, value] of utmEntries) {
      if (value) utm[field] = value;
    }

    const ctx: ClientContext = {};
    if (pageUrl) ctx.pageUrl = pageUrl;
    if (referrer) ctx.referrer = referrer;
    if (Object.keys(utm).length > 0) ctx.utm = utm;

    return Object.keys(ctx).length > 0 ? ctx : undefined;
  }, [searchParams]);

  // Stable anonymous visitor id. The embed loader mints + forwards it as a URL
  // param from the HOST origin (reliable, unpartitioned localStorage). When it's
  // absent — direct iframe embeds and the admin widget preview — we mint + persist
  // one in THIS iframe's own localStorage so repeat loads still dedupe to one
  // Customer. Skipped when an identified user is present (externalId wins; no anon
  // record needed).
  const anonymousId = useMemo<string | undefined>(() => {
    if (user?.externalId) return undefined;
    const fromParam = searchParams.get('anonymousId');
    if (fromParam) return fromParam;
    const apiKey = searchParams.get('apiKey');
    return apiKey ? getOrCreateAnonymousId(apiKey) : undefined;
  }, [searchParams, user]);

  if (!agentId) {
    // Defensive: react-router won't actually let us reach this with no agentId,
    // but the type is `string | undefined` so we narrow.
    return null;
  }

  return (
    <ChatPage
      agentId={agentId}
      theme={theme}
      // `?mode=voice` opens straight into a call — for embedders whose
      // entry point is a phone/call affordance rather than a chat bubble.
      autoStartVoice={searchParams.get('mode') === 'voice'}
      branding={searchParams.get('branding') !== 'false'}
      privacyUrl={searchParams.get('privacyUrl') ?? undefined}
      locale={searchParams.get('locale') ?? undefined}
      agentName={searchParams.get('name') ?? undefined}
      agentSubtitle={searchParams.get('subtitle') ?? undefined}
      agentInitials={searchParams.get('initials') ?? undefined}
      agentAvatarUrl={searchParams.get('avatar') ?? undefined}
      greeting={searchParams.get('greeting') ?? undefined}
      placeholder={searchParams.get('placeholder') ?? undefined}
      darkBg={searchParams.get('darkBg') ?? undefined}
      apiKey={searchParams.get('apiKey') ?? undefined}
      baseUrl={searchParams.get('baseUrl') ?? undefined}
      user={user}
      userHash={userHash}
      anonymousId={anonymousId}
      clientContext={clientContext}
    />
  );
}
